import type { ComponentType } from "react";
import { TILE_META } from "@/lib/tileIds";
import { CashTile } from "@/components/tiles/CashTile";
import { NextPayrollTile } from "@/components/tiles/NextPayrollTile";
import { NeedsYouTile } from "@/components/tiles/NeedsYouTile";
import { BillsDueTile } from "@/components/tiles/BillsDueTile";
import { ActivityTile } from "@/components/tiles/ActivityTile";
import { RemindersTile } from "@/components/tiles/RemindersTile";
// @tile-registry-anchor — a future tile adds ONE import above and ONE entry in COMPONENTS below.

/**
 * ef-061 anchor (Spec Architect, FROZEN) — the web tile registry, built the way `reportRegistry.ts` is: data
 * about each tile (ids/titles/order: `tileIds.ts`, mirrored by the server's `HomeTileRegistry.cs`) plus the
 * component that renders it. Dashboard.tsx (ef-061-4) renders `useHomeLayout().tiles` by looking each id up
 * here; an id with no component here is skipped. Each component takes NO props: whatever Home data a tile
 * shares with its siblings comes from the `HomeShared` context ef-061-4 builds (`components/tiles/HomeShared.tsx`);
 * a tile that reads its own data (Bills due, Reminders) fetches it itself.
 *
 * No slice edits this file: the six components below are stubs that the slices REPLACE in place.
 */
const COMPONENTS: Record<string, ComponentType> = {
  cash: CashTile,
  "next-payroll": NextPayrollTile,
  "needs-you": NeedsYouTile,
  "bills-due": BillsDueTile,
  activity: ActivityTile,
  reminders: RemindersTile,
};

export interface TileDef {
  id: string;
  title: string;
  component: ComponentType;
}

export const TILES: readonly TileDef[] = TILE_META.map((m) => ({
  ...m,
  component: COMPONENTS[m.id] as ComponentType,
}));

export function tileById(id: string): TileDef | undefined {
  return TILES.find((t) => t.id === id);
}
